import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { 
  MapPin, 
  Phone, 
  CheckCircle2, 
  ArrowRight, 
  Home,
  Wrench,
  Building2,
  Star,
  Clock,
  Shield
} from 'lucide-react';

// Location data for each service area
const LOCATIONS = {
  'concord': {
    city: 'Concord',
    county: 'Cabarrus County',
    state: 'NC',
    tagline: 'Your Trusted Custom Home Builder in Concord',
    description: 'C3 Contracting is Concord\'s premier custom home builder and renovation contractor. Serving the heart of Cabarrus County, we bring faith-based values and exceptional craftsmanship to every project.',
    nearbyAreas: ['Kannapolis', 'Harrisburg', 'Mount Pleasant', 'China Grove'],
    highlights: [
      'Local team based right here in Concord',
      'Deep knowledge of Cabarrus County building codes',
      'Trusted by Concord families since 2022',
      'Licensed NC General Contractor'
    ],
    heroImage: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200',
    zipCodes: ['28025', '28026', '28027'],
  },
  'kannapolis': {
    city: 'Kannapolis',
    county: 'Cabarrus County',
    state: 'NC',
    tagline: 'Custom Home Building & Renovations in Kannapolis',
    description: 'From new custom homes to complete renovations, C3 Contracting serves Kannapolis homeowners with quality craftsmanship and personalized service.',
    nearbyAreas: ['Concord', 'Landis', 'China Grove', 'Enochville'],
    highlights: [
      'Serving Kannapolis and surrounding communities',
      'Experts in both new construction and renovations',
      'Local references available',
      'Transparent pricing with no surprises'
    ],
    heroImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200',
    zipCodes: ['28081', '28083'],
  },
  'mooresville': {
    city: 'Mooresville',
    county: 'Iredell County',
    state: 'NC',
    tagline: 'Lake Norman\'s Premier Custom Home Builder',
    description: 'Building dream homes in Mooresville and the Lake Norman area. C3 Contracting specializes in luxury custom homes and high-end renovations for discerning homeowners.',
    nearbyAreas: ['Davidson', 'Cornelius', 'Denver', 'Troutman'],
    highlights: [
      'Expertise in Lake Norman waterfront properties',
      'High-end finishes and custom details',
      'Design-build services for seamless execution',
      'Experienced with Iredell County permitting'
    ],
    heroImage: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200',
    zipCodes: ['28115', '28117'],
  },
  'davidson': {
    city: 'Davidson',
    county: 'Mecklenburg County',
    state: 'NC',
    tagline: 'Boutique Custom Home Building in Davidson',
    description: 'Davidson homeowners trust C3 Contracting for custom homes and renovations that honor the town\'s unique character while delivering modern comfort and efficiency.',
    nearbyAreas: ['Cornelius', 'Huntersville', 'Mooresville', 'Lake Norman'],
    highlights: [
      'Sensitive to Davidson\'s historic character',
      'Experience with HOA requirements',
      'Energy-efficient building practices',
      'Personalized design-build process'
    ],
    heroImage: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=1200',
    zipCodes: ['28036'],
  },
  'china-grove': {
    city: 'China Grove',
    county: 'Rowan County',
    state: 'NC',
    tagline: 'Quality Home Building in China Grove',
    description: 'Serving China Grove and Rowan County with honest, quality construction. From custom homes to renovations, C3 Contracting delivers exceptional results.',
    nearbyAreas: ['Landis', 'Kannapolis', 'Salisbury', 'Rockwell'],
    highlights: [
      'Serving the China Grove community',
      'Familiar with Rowan County building requirements',
      'Competitive pricing without compromising quality',
      'Full-service design and construction'
    ],
    heroImage: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=1200',
    zipCodes: ['28023'],
  },
};

// Services offered
const SERVICES = [
  {
    icon: Home,
    title: 'Custom Home Building',
    description: 'Design and build your dream home from the ground up with our full-service custom home building.',
  },
  {
    icon: Wrench,
    title: 'Whole-Home Renovations',
    description: 'Transform your existing home with comprehensive renovations that modernize every aspect.',
  },
  {
    icon: Building2,
    title: 'Home Additions',
    description: 'Expand your living space with seamlessly integrated additions that match your home\'s character.',
  },
];

const LocationPage = () => {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);

  useEffect(() => {
    const loc = LOCATIONS[slug];
    if (loc) {
      setLocation(loc);
      // Update page title for SEO
      document.title = `Custom Home Builder ${loc.city} NC | C3 Contracting | ${loc.county}`;
    }
  }, [slug]);

  if (!location) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-52 pb-20 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Location not found</h1>
          <Link to="/services" className="text-cyan-600 hover:underline mt-4 inline-block">
            View all service areas
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url(${location.heroImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-cyan-400 mb-4">
            <MapPin className="w-5 h-5" />
            <span className="text-sm font-medium">{location.county}, {location.state}</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Custom Home Builder in {location.city}, NC
          </h1>
          
          <p className="text-xl text-slate-300 max-w-3xl mb-8">
            {location.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8">
              <Link to="/consultation">Schedule a Consultation</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-semibold px-8">
              <a href="tel:980-680-6703">
                <Phone className="w-4 h-4 mr-2" />
                (980) 680-6703
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Why {location.city} Homeowners Choose C3 Contracting
              </h2>
              <div className="space-y-4">
                {location.highlights.map((highlight, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-lg">{highlight}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 p-6 bg-slate-50 rounded-xl">
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <span className="text-slate-600 font-medium">5-Star Rated</span>
                </div>
                <p className="text-slate-600 italic">
                  "C3 Contracting exceeded our expectations on our custom home in {location.city}. 
                  Professional, communicative, and the craftsmanship is outstanding."
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-cyan-50 rounded-2xl p-6 text-center">
                <Clock className="w-10 h-10 text-cyan-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-slate-900">3+</div>
                <div className="text-slate-600">Years Serving {location.city}</div>
              </div>
              <div className="bg-cyan-50 rounded-2xl p-6 text-center">
                <Home className="w-10 h-10 text-cyan-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-slate-900">50+</div>
                <div className="text-slate-600">Projects Completed</div>
              </div>
              <div className="bg-cyan-50 rounded-2xl p-6 text-center">
                <Shield className="w-10 h-10 text-cyan-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-slate-900">100%</div>
                <div className="text-slate-600">Licensed & Insured</div>
              </div>
              <div className="bg-cyan-50 rounded-2xl p-6 text-center">
                <Star className="w-10 h-10 text-cyan-600 mx-auto mb-3" />
                <div className="text-3xl font-bold text-slate-900">5.0</div>
                <div className="text-slate-600">Google Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Our Services in {location.city}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From custom home construction to complete renovations, we offer comprehensive 
              building services throughout {location.county}.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {SERVICES.map((service, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                <p className="text-slate-600 mb-4">{service.description}</p>
                <Link 
                  to="/services" 
                  className="text-cyan-600 font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                >
                  Learn More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Serving {location.city} and Nearby Communities
            </h2>
            <p className="text-lg text-slate-600">
              In addition to {location.city}, we proudly serve these neighboring areas:
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {location.nearbyAreas.map((area, idx) => (
              <div 
                key={idx}
                className="px-6 py-3 bg-slate-100 rounded-full text-slate-700 font-medium"
              >
                <MapPin className="w-4 h-4 inline mr-2 text-cyan-600" />
                {area}, NC
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-2">
              <strong>Zip Codes Served:</strong> {location.zipCodes.join(', ')}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-cyan-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Build Your Dream Home in {location.city}?
          </h2>
          <p className="text-lg text-cyan-100 mb-8">
            Let&apos;s discuss your project. Schedule a free consultation and get a detailed proposal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold px-8"
            >
              <Link to="/consultation">Get Free Consultation</Link>
            </Button>
            <Button 
              asChild
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white/10 font-semibold px-8"
            >
              <Link to="/portfolio">View Our Work</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Schema.org LocalBusiness JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HomeAndConstructionBusiness",
          "name": `C3 Contracting - ${location.city}`,
          "description": location.description,
          "url": `https://c3-contracting.com/locations/${slug}`,
          "telephone": "980-680-6703",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": location.city,
            "addressRegion": "NC",
            "addressCountry": "US"
          },
          "areaServed": {
            "@type": "City",
            "name": location.city,
            "containedInPlace": {
              "@type": "AdministrativeArea",
              "name": location.county
            }
          },
          "priceRange": "$$$$",
          "openingHours": "Mo-Fr 08:00-17:00",
          "sameAs": [
            "https://www.facebook.com/c3contracting",
            "https://www.instagram.com/c3contracting"
          ]
        })
      }} />

      <Footer />
    </div>
  );
};

export default LocationPage;
